import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  LinkIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { driveClient } from '@/lib/drive/driveClient';
import { API_URL } from '@/lib/api';
import type { ActivityRow, SharedWithMeRow, ShareRow } from '@/lib/drive/driveClient';
import { formatBytes, formatTimestamp } from '@/lib/drive/format';

/**
 * The dashboard's secondary views — "Shared with me", "My shares" and the owner's
 * own activity feed. Each fetches through the drive client and answers its own
 * loading state, so the drive hook stays about the file listing.
 */

function PanelFrame({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-48 flex-1 flex-col overflow-hidden rounded-lg border border-border-color bg-card">{children}</div>;
}

function Loading() {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-border-color border-t-accent" />
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

/* -------------------------------------------------------------- shared with me */

export function SharedWithMeView() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SharedWithMeRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setRows(await driveClient.listSharedWithMe());
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setRows([]);
      }
    })();
  }, []);

  const open = (row: SharedWithMeRow) => {
    const url = `${driveOrigin()}/api/shares/${row.shortId}/download`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <PanelFrame>
      <div className="flex flex-nowrap items-center gap-2 border-b border-border-color px-4 py-2.5">
        <UserGroupIcon className="h-4 w-4 flex-shrink-0 text-accent" />
        <h2 className="min-w-0 truncate whitespace-nowrap text-sm font-medium text-foreground">{t('drive.sharedWithMe')}</h2>
      </div>
      {rows === null ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty label={error ?? t('drive.sharedWithMeEmpty')} />
      ) : (
        <ul className="divide-y divide-border-color overflow-y-auto">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-nowrap items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate whitespace-nowrap text-sm text-foreground" title={row.fileName ?? ''}>
                  {row.fileName ?? '—'}
                </p>
                <p className="whitespace-nowrap text-xs text-muted-foreground">
                  {row.ownerName ?? row.ownerEmail ?? ''} · {formatBytes(row.fileSize ?? 0)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => open(row)}
                className="flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {t('drive.open')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </PanelFrame>
  );
}

/* ---------------------------------------------------------------- my shares */

export function MySharesView() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<ShareRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await driveClient.listShares());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (row: ShareRow) => {
    try {
      await driveClient.revokeShare(row.id);
      toast.success(t('drive.shareRevoked'));
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const copyLink = async (row: ShareRow) => {
    await navigator.clipboard.writeText(`${driveOrigin()}/api/shares/${row.shortId}/download`).catch(() => undefined);
    toast.success(t('drive.linkCopied', { url: '' }));
  };

  return (
    <PanelFrame>
      <div className="flex flex-nowrap items-center gap-2 border-b border-border-color px-4 py-2.5">
        <LinkIcon className="h-4 w-4 flex-shrink-0 text-accent" />
        <h2 className="min-w-0 truncate whitespace-nowrap text-sm font-medium text-foreground">{t('drive.myShares')}</h2>
      </div>
      {rows === null ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty label={error ?? t('drive.mySharesEmpty')} />
      ) : (
        <div className="flex-1 overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border-color text-xs uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="px-3 py-2 text-start font-medium">{t('drive.name')}</th>
                <th scope="col" className="w-24 px-3 py-2 text-start font-medium">{t('drive.downloads')}</th>
                <th scope="col" className="w-28 px-3 py-2 text-start font-medium">{t('drive.shareExpiry')}</th>
                <th scope="col" className="w-36 px-3 py-2 text-end font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-border-color/50 last:border-b-0 hover:bg-muted">
                  <td className="min-w-0 px-3 py-2">
                    <span className="block truncate whitespace-nowrap text-foreground" title={row.fileName ?? ''}>
                      {row.fileName ?? '—'}
                    </span>
                    <span className="block whitespace-nowrap text-xs text-muted-foreground">
                      {row.sharedWithEmail ? t('drive.sharedTo', { email: row.sharedWithEmail }) : ''}
                      {row.requiresPassword ? ` · ${t('drive.sharePasswordTag')}` : ''}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {row.downloads}
                    {row.maxDownloads ? ` / ${row.maxDownloads}` : ''}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                    {row.expiresAt ? formatTimestamp(new Date(row.expiresAt).getTime(), i18n.resolvedLanguage) : '∞'}
                  </td>
                  <td className="px-3 py-2 text-end">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => void copyLink(row)}
                        className="whitespace-nowrap rounded-md px-2 py-1 text-xs text-foreground transition-colors hover:bg-muted"
                      >
                        {t('drive.copy')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void revoke(row)}
                        className="whitespace-nowrap rounded-md px-2 py-1 text-xs text-destructive transition-colors hover:bg-muted"
                      >
                        {t('drive.revoke')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PanelFrame>
  );
}

/* ---------------------------------------------------------------- activity */

const ACTIVITY_ICONS: Record<string, string> = {
  FILE_UPLOAD: '↥',
  FILE_DELETE: '↧',
  FOLDER_CREATE: '＋',
  FILE_SHARE: '⇄',
};

export function ActivityView() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<ActivityRow[] | null>(null);
  const [days, setDays] = useState('30');

  const load = useCallback(async (window_days: string) => {
    setRows(null);
    try {
      setRows(await driveClient.listActivity(Number(window_days) || 30));
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  return (
    <PanelFrame>
      <div className="flex flex-nowrap items-center justify-between gap-3 border-b border-border-color px-4 py-2.5">
        <h2 className="min-w-0 truncate whitespace-nowrap text-sm font-medium text-foreground">{t('drive.activity')}</h2>
        <select
          value={days}
          onChange={(event) => setDays(event.target.value)}
          className="flex-shrink-0 whitespace-nowrap rounded-md border border-border-color bg-background px-2 py-1 text-xs text-foreground"
        >
          <option value="7">{t('drive.activity7')}</option>
          <option value="30">{t('drive.activity30')}</option>
          <option value="90">{t('drive.activity90')}</option>
        </select>
      </div>
      {rows === null ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty label={t('drive.activityEmpty')} />
      ) : (
        <ul className="divide-y divide-border-color overflow-y-auto">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-nowrap items-center gap-3 px-4 py-2.5">
              <span
                aria-hidden="true"
                className={clsx(
                  'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs text-foreground',
                )}
              >
                {ACTIVITY_ICONS[row.type] ?? '•'}
              </span>
              <p className="min-w-0 flex-1 truncate whitespace-nowrap text-sm text-foreground">{row.description}</p>
              <p className="flex-shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                {formatTimestamp(new Date(row.createdAt).getTime(), i18n.resolvedLanguage)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </PanelFrame>
  );
}

/** The drive origin — the pooled API's own base, which is where public share routes live. */
function driveOrigin(): string {
  // API_URL already ends in /api/vault (or / in standalone); share URLs ride its /api prefix.
  return API_URL.replace(/\/api\/vault$/, '').replace(/\/$/, '');
}
